import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { inr, todayISO, formatDate, GST_OPTIONS } from "@/lib/utils2";

export default function BillCreate() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [parties, setParties] = useState([]);
  const [filter, setFilter] = useState({ party_id: "", bill_type: "material", date_from: "", date_to: "" });
  const [trucks, setTrucks] = useState([]);
  const [selected, setSelected] = useState({}); // truck_id -> {checked, weight, bill_rate, description}
  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(todayISO());
  const [gst, setGst] = useState(5);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => setParties((await api.get("/parties")).data))(); }, []);

  // Auto-suggest bill number when type changes (still editable)
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/bills/next-number", { params: { bill_type: filter.bill_type } });
      setBillNumber(data.suggested);
    })();
  }, [filter.bill_type]);

  const fetchTrucks = async () => {
    if (!filter.party_id) return toast.error("Select a party first");
    const params = {
      party_id: filter.party_id,
      for_bill: filter.bill_type,
      status: "unbilled",
    };
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    const { data } = await api.get("/trucks", { params });
    setTrucks(data);
    setSelected({});
    setStep(2);
  };

  const defaultsFor = (t) => {
    if (filter.bill_type === "material") {
      return { w: t.material_weight, r: t.material_bill_rate };
    }
    if (filter.bill_type === "combined") {
      return { w: t.material_weight || t.transport_weight, r: t.combined_bill_rate };
    }
    return { w: t.transport_weight, r: t.transport_bill_rate };
  };

  const toggleAll = (checked) => {
    if (checked) {
      const all = {};
      trucks.forEach((t) => {
        const { w, r } = defaultsFor(t);
        all[t.id] = { checked: true, weight: w, bill_rate: r, description: "" };
      });
      setSelected(all);
    } else setSelected({});
  };

  const toggle = (t, checked) => {
    const { w, r } = defaultsFor(t);
    setSelected((s) => ({
      ...s,
      [t.id]: checked ? { checked: true, weight: w, bill_rate: r, description: "" } : { ...s[t.id], checked: false }
    }));
  };

  const lineUpdate = (id, k, v) => setSelected((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));

  const chosenLines = useMemo(() => {
    return trucks.filter((t) => selected[t.id]?.checked).map((t) => {
      const e = selected[t.id];
      const w = parseFloat(e.weight) || 0;
      const r = parseFloat(e.bill_rate) || 0;
      return { truck: t, weight: w, bill_rate: r, amount: +(w * r).toFixed(2), description: e.description || "" };
    });
  }, [trucks, selected]);

  const subtotal = +chosenLines.reduce((s, l) => s + l.amount, 0).toFixed(2);
  const gstAmt = +(subtotal * gst / 100).toFixed(2);
  const total = Math.round(subtotal + gstAmt);

  const save = async () => {
    if (chosenLines.length === 0) return toast.error("Select at least one truck");
    if (!billNumber.trim()) return toast.error("Bill number is required");
    setSaving(true);
    try {
      const payload = {
        bill_number: billNumber.trim(),
        bill_date: billDate,
        bill_type: filter.bill_type,
        party_id: filter.party_id,
        gst_percent: Number(gst),
        truck_ids: chosenLines.map((l) => l.truck.id),
        line_edits: chosenLines.map((l) => ({
          truck_id: l.truck.id, weight: l.weight, bill_rate: l.bill_rate,
          description: l.description || null
        })),
        notes,
      };
      const { data } = await api.post("/bills", payload);
      toast.success("Bill created");
      nav(`/bills/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create bill");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] mx-auto" data-testid="bill-create-page">
      <PageHeader title="Create Bill" subtitle="Material · Transport · Combined invoices — each kept separate. Pick a party, narrow by date, then choose trucks." />

      {/* Step 1 */}
      <Card className="rounded-sm border-brand-line shadow-none p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">1. Filter trucks</div>
          {step === 2 && <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="rounded-sm">← Change filter</Button>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <Label>Party *</Label>
            <Select value={filter.party_id} onValueChange={(v) => setFilter({ ...filter, party_id: v })}>
              <SelectTrigger data-testid="bc-party-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue placeholder="Select party" /></SelectTrigger>
              <SelectContent>
                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bill Type</Label>
            <Select value={filter.bill_type} onValueChange={(v) => setFilter({ ...filter, bill_type: v })}>
              <SelectTrigger data-testid="bc-type-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="material">Material</SelectItem>
                <SelectItem value="transport">Transport</SelectItem>
                <SelectItem value="combined">Combined (inclusive rate)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>From</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} /></div>
          <div><Label>To</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} /></div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={fetchTrucks} data-testid="bc-fetch-trucks-btn" className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">Fetch unbilled trucks</Button>
        </div>
      </Card>

      {step >= 2 && (
        <>
          {/* Step 2: pick trucks */}
          <Card className="rounded-sm border-brand-line shadow-none mb-6 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">2. Select Trucks</div>
                <div className="font-serif text-xl text-brand-ink mt-1">
                  {trucks.length} matching {filter.bill_type} trucks
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  data-testid="select-all-trucks"
                  checked={trucks.length > 0 && chosenLines.length === trucks.length}
                  onCheckedChange={(v) => toggleAll(!!v)}
                />
                <span className="text-xs uppercase tracking-[0.15em] text-brand-muted">Select all</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                  <tr>
                    <th className="px-5 py-3"></th>
                    <th className="text-left px-3 py-3">Date</th>
                    <th className="text-left px-3 py-3">Vehicle</th>
                    <th className="text-right px-3 py-3">Weight</th>
                    <th className="text-right px-3 py-3">Rate</th>
                    <th className="text-right px-3 py-3">Amount</th>
                    <th className="text-left px-3 py-3 w-[20%]">Description (optional)</th>
                  </tr>
                </thead>
                <tbody>
                  {trucks.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-brand-muted text-sm">No unbilled trucks match these filters.</td></tr>}
                  {trucks.map((t) => {
                    const e = selected[t.id] || {};
                    const checked = !!e.checked;
                    const { w: defaultW, r: defaultR } = defaultsFor(t);
                    const w = parseFloat(e.weight ?? defaultW) || 0;
                    const r = parseFloat(e.bill_rate ?? defaultR) || 0;
                    return (
                      <tr key={t.id} className={`border-t border-brand-line ${checked ? "bg-brand-sand/10" : ""}`} data-testid={`bc-truck-${t.id}`}>
                        <td className="px-5 py-3"><Checkbox checked={checked} onCheckedChange={(v) => toggle(t, !!v)} data-testid={`select-truck-${t.id}`} /></td>
                        <td className="px-3 py-3 whitespace-nowrap">{formatDate(t.entry_date)}</td>
                        <td className="px-3 py-3 font-medium">{t.vehicle_number}</td>
                        <td className="px-3 py-3 text-right">
                          {checked
                            ? <Input type="number" step="0.001" value={e.weight} onChange={(ev) => lineUpdate(t.id, "weight", ev.target.value)} className="h-8 w-24 rounded-sm text-right tabular-nums bg-white" />
                            : <span className="tabular-nums">{defaultW}</span>}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {checked
                            ? <Input type="number" step="0.01" value={e.bill_rate} onChange={(ev) => lineUpdate(t.id, "bill_rate", ev.target.value)} className="h-8 w-24 rounded-sm text-right tabular-nums bg-white" />
                            : <span className="tabular-nums">{defaultR}</span>}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-medium">{inr(w * r)}</td>
                        <td className="px-3 py-3">
                          {checked && <Input placeholder="override description…" value={e.description || ""} onChange={(ev) => lineUpdate(t.id, "description", ev.target.value)} className="h-8 rounded-sm bg-white" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Step 3: bill meta + summary */}
          {chosenLines.length > 0 && (
            <Card className="rounded-sm border-brand-line shadow-none p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-5">3. Bill Details</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <Label>Bill Number (editable) *</Label>
                  <Input data-testid="bc-bill-number" value={billNumber} onChange={(e) => setBillNumber(e.target.value)} className="mt-1.5 h-10 rounded-sm bg-white font-mono" />
                </div>
                <div>
                  <Label>Bill Date *</Label>
                  <Input type="date" data-testid="bc-bill-date" value={billDate} onChange={(e) => setBillDate(e.target.value)} className="mt-1.5 h-10 rounded-sm bg-white" />
                </div>
                <div>
                  <Label>GST %</Label>
                  <Select value={String(gst)} onValueChange={(v) => setGst(Number(v))}>
                    <SelectTrigger data-testid="bc-gst-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GST_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 h-10 rounded-sm bg-white" placeholder="Optional" />
                </div>
              </div>

              <div className="border-t border-brand-line pt-5 grid grid-cols-2 md:grid-cols-4 gap-5">
                <Summary label="Lines" value={chosenLines.length} />
                <Summary label="Subtotal" value={inr(subtotal)} />
                <Summary label={`GST @ ${gst}%`} value={inr(gstAmt)} />
                <Summary label="Total Payable" value={inr(total)} highlight />
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={() => nav(-1)} className="rounded-sm">Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="bc-save-bill-btn" className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
                  {saving ? "Saving…" : "Save Bill"}
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Summary({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">{label}</div>
      <div className={`mt-1 font-serif text-2xl tabular-nums ${highlight ? "text-brand-forest" : "text-brand-ink"}`}>{value}</div>
    </div>
  );
}
